import { useService } from '@old/hooks/use-service';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { listUsersService } from '@old/services/domain/user/base/user-list';
import { Countryside } from '@old/typed/domain/base/countryside.types';
import { PropsWithChildren, useMemo, useRef, useState } from 'react';
import i18n from '../../../libs/language';
import { updateUserDataService } from '@old/services/domain/user/base/user-edition';
import { CountrysidePermission } from '@old/typed/domain/permission.types';
import { useEffectOnUpdate, useToggle } from '@old/hooks/core';
import { useGlobalStore } from '@old/global-store/use-store';
import { ToastAction } from '@old/ui/toast/toast-action';

type Permission = Pick<CountrysidePermission, 'read_permission' | 'write_permission' | 'full_permission'>;
type AvailableFilters = keyof Pick<
  User['v_countrysides_permissions'][0],
  keyof Permission | 'inherited' | 'is_guest'
>;
type Params = {
  userID: User['id'];
  permission: Permission;
};

type User = Awaited<ReturnType<typeof listUsersService>>['summary'][0];

type Props = {
  open: boolean;
  onClose: (b: boolean) => void;
  countryside: {
    id?: Countryside['id'] | null;
    name?: string | null;
  };
  reFetchCountrysideListData: () => void;
  read: boolean;
};
export function CountrysidePermissionModal({
  open,
  onClose,
  countryside,
  reFetchCountrysideListData,
  read,
}: Props) {
  const { userID } = useGlobalStore();

  const needRefetch = useRef(false);
  const hasChangeOwnPermissions = useRef(false);

  const { summary, isLoading: isListUsersLoading } = useService(listUsersService, { fetchOnMount: true });
  const {
    // isLoading: isUpdateLoading, //
    isError: isErrorUpdateUser,
    // error: userUpdateError,
    trigger: triggerUpdateUserService,
  } = useService(updateUserDataService, {
    onSuccess: ({ response }) => {
      setUsers((prev) => {
        if (!prev) return prev;

        const idx = prev.findIndex((u) => u.id === response.id);
        const find = prev[idx];
        if (!find) return prev;

        const newUser: User = {
          ...find,
          // @ts-expect-error no matchean los tipos de User y EditedUser y es muy rompebolas TypeScript
          v_countrysides_permissions: response.countryside_permission,
        };
        return [...prev.slice(0, idx), newUser, ...prev.slice(idx + 1)];
      });
      deactivateUserIDLoader(response.id);
      needRefetch.current = true;
      if (response.id === userID) hasChangeOwnPermissions.current = true;
    },
    onError: (error) => {
      const msg = error.data.message;

      if (msg && typeof msg === 'object' && 'details' in msg) {
        const details = msg.details as { user_id: number };
        deactivateUserIDLoader(details.user_id);
      } else {
        deactiveAllUserLoaders();
      }
    },
  });

  const [lockOwnPermission, setLockOwnPermission] = useToggle(true);
  const [permissionStats, setPermissionStats] = useState({
    read: 0,
    write: 0,
    full: 0,
    inherit: 0,
    guest: 0,
    empty: 0,
  });
  const [users, setUsers] = useState<User[] | undefined>(undefined);
  const [filter, setFilter] = useState<AvailableFilters | null>(null);
  const toggleFilter = (f: AvailableFilters) => setFilter((prev) => (prev !== f ? f : null));
  const removeFilter = () => setFilter(null);
  const isFilterActive = filter != null;
  const [ownUserChanges, setOwnUserChanges] = useState<{ userID: number; permission: Permission } | null>(
    null
  );
  const [loadingUsersID, setLoadingUsersID] = useState<Set<User['id']>>(new Set());
  const activeUserIDLoader = (id: User['id']) =>
    setLoadingUsersID((prev) => {
      const newSet = new Set(prev);
      newSet.add(id);
      return newSet;
    });
  const deactivateUserIDLoader = (id: User['id']) =>
    setLoadingUsersID((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  const deactiveAllUserLoaders = () => setLoadingUsersID(new Set());

  useEffectOnUpdate(() => {
    if (!summary) return;

    const newUsers = summary.map((rawUser) => ({
      ...rawUser,
      v_countrysides_permissions: [
        rawUser.v_countrysides_permissions.find((cs) => cs.countryside_id === countryside.id)!,
      ],
    }));

    setUsers(newUsers.sort((a, b) => a.fullname.localeCompare(b.fullname)));
  }, [summary]);

  useEffectOnUpdate(() => {
    if (!users) return;

    const newStats = {
      read: 0,
      write: 0,
      full: 0,
      inherit: 0,
      guest: 0,
      empty: 0,
    };

    for (const user of users) {
      const permission = user.v_countrysides_permissions.at(0);
      if (!permission) {
        newStats.empty += 1;
        continue;
      }
      if (permission.read_permission) newStats.read += 1;
      if (permission.write_permission) newStats.write += 1;
      if (permission.full_permission) newStats.full += 1;
      if (permission.inherited) newStats.inherit += 1;
      if (permission.is_guest) newStats.guest += 1;
    }

    setPermissionStats(newStats);
  }, [users]);

  const updateUserPermissions = (param: Params) => {
    const { userID } = param;
    const { permission } = param;
    const body = {
      user_countrysides_permissions: [
        {
          countryside_id: countryside.id,
          ...permission,
        },
      ],
    };
    triggerUpdateUserService({
      query: userID,
      body,
    });
  };

  const filteredUsers = useMemo(() => {
    if (!isFilterActive) return users;

    if (filter === 'read_permission')
      return users?.filter(
        (u) =>
          u.v_countrysides_permissions.at(0)?.read_permission &&
          !u.v_countrysides_permissions.at(0)?.write_permission
      );

    if (filter === 'write_permission')
      return users?.filter(
        (u) =>
          u.v_countrysides_permissions.at(0)?.write_permission &&
          !u.v_countrysides_permissions.at(0)?.full_permission
      );

    return users?.filter((u) => u.v_countrysides_permissions.at(0)?.[filter]);
  }, [users, filter]);

  return (
    <MyModal open={open} onClose={() => {}}>
      {isErrorUpdateUser ? (
        <ToastAction message={i18n.t('app.user_countryside_modal.no_permissions_toast')} severity="error" />
      ) : null}
      <Stack flex={1} gap={2} style={{ overflow: 'auto' }}>
        <Stack flex={1} gap={2} style={{ overflow: 'auto' }}>
          <Stack direction="row" gap={8} alignItems="center">
            <Stack direction="row" gap={1} alignItems="center">
              <Typography
                fontSize={11}
                color="textSecondary"
                sx={{
                  backgroundColor: '#EEE',
                  border: '1px solid #A7A7A7',
                  borderRadius: 1,
                  p: '4px',
                  userSelect: 'none',
                }}
              >
                {i18n.t('app.countrysides')}
              </Typography>
              <Typography fontSize={16} fontWeight={800} style={{ textWrap: 'balance' }}>
                {countryside.name}
              </Typography>
            </Stack>
            <Stack direction="row" gap={2} alignItems="center">
              <Typography
                fontSize={11}
                color="textSecondary"
                sx={{
                  backgroundColor: '#EEE',
                  border: '1px solid #A7A7A7',
                  borderRadius: 1,
                  p: '4px',
                  userSelect: 'none',
                }}
              >
                {i18n.t('app.user_modal.users')}
              </Typography>
              <Button
                style={{
                  minWidth: 0,
                  padding: 4,
                  border: !isFilterActive ? '1px solid #6baa00' : '',
                  borderRadius: '8px',
                  backgroundColor: !isFilterActive ? '#6baa0033' : '',
                  textTransform: 'none',
                }}
                onClick={removeFilter}
              >
                <Stack minWidth="48px" alignItems="center">
                  <Typography fontSize={14} fontWeight={800} color="primary">
                    {permissionStats.read}
                  </Typography>
                  <Typography
                    fontSize={11}
                    color="textSecondary"
                    style={{ color: '#6baa00', width: 'max-content' }}
                  >
                    {i18n.t('app.report_devices.totals')}
                  </Typography>
                </Stack>
              </Button>
              <Divider flexItem orientation="vertical" />
              <Button
                disabled={permissionStats.read - permissionStats.write === 0}
                sx={{
                  minWidth: 0,
                  padding: '4px',
                  border: filter === 'read_permission' ? '1px solid #777' : '',
                  borderRadius: '8px',
                  color: 'black',
                  backgroundColor: filter === 'read_permission' ? '#eee' : '',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#dedede',
                  },
                }}
                onClick={() => toggleFilter('read_permission')}
              >
                <Stack minWidth="48px" alignItems="center">
                  <Typography fontSize={14} fontWeight={800}>
                    {permissionStats.read - permissionStats.write}
                  </Typography>
                  <Typography fontSize={11} color="textSecondary" style={{ width: 'max-content' }}>
                    {i18n.t('app.silobag_share.view')}
                  </Typography>
                </Stack>
              </Button>
              <Button
                disabled={permissionStats.write - permissionStats.full === 0}
                sx={{
                  minWidth: 0,
                  padding: '4px',
                  border: filter === 'write_permission' ? '1px solid #777' : '',
                  borderRadius: '8px',
                  color: 'black',
                  backgroundColor: filter === 'write_permission' ? '#eee' : '',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#dedede',
                  },
                }}
                onClick={() => toggleFilter('write_permission')}
              >
                <Stack minWidth="48px" alignItems="center">
                  <Typography fontSize={14} fontWeight={800}>
                    {permissionStats.write - permissionStats.full}
                  </Typography>
                  <Typography fontSize={11} color="textSecondary" style={{ width: 'max-content' }}>
                    {i18n.t('app.edit')}
                  </Typography>
                </Stack>
              </Button>
              <Button
                disabled={permissionStats.full === 0}
                sx={{
                  minWidth: 0,
                  padding: '4px',
                  border: filter === 'full_permission' ? '1px solid #777' : '',
                  borderRadius: '8px',
                  color: 'black',
                  backgroundColor: filter === 'full_permission' ? '#eee' : '',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#dedede',
                  },
                }}
                onClick={() => toggleFilter('full_permission')}
              >
                <Stack minWidth="48px" alignItems="center">
                  <Typography fontSize={14} fontWeight={800}>
                    {permissionStats.full}
                  </Typography>
                  <Typography fontSize={11} color="textSecondary" style={{ width: 'max-content' }}>
                    {i18n.t('app.silobag_share.access_complete')}
                  </Typography>
                </Stack>
              </Button>
              <Divider flexItem orientation="vertical" />
              <Button
                disabled={permissionStats.inherit === 0}
                sx={{
                  minWidth: 0,
                  padding: '4px',
                  border: filter === 'inherited' ? '1px solid #22A7F0' : '',
                  borderRadius: '8px',
                  color: permissionStats.inherit ? '#22A7F0' : 'black',
                  backgroundColor: filter === 'inherited' ? '#22A7F033' : '',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#22A7F01A',
                  },
                }}
                onClick={() => toggleFilter('inherited')}
              >
                <Stack minWidth="48px" alignItems="center">
                  <Typography fontSize={14} fontWeight={800}>
                    {permissionStats.inherit}
                  </Typography>
                  <Typography fontSize={11} style={{ width: 'max-content' }}>
                    {i18n.t('app.user_countryside_modal.inherited')}
                  </Typography>
                </Stack>
              </Button>
              <Button
                disabled={permissionStats.guest === 0}
                sx={{
                  minWidth: 0,
                  padding: '4px',
                  border: filter === 'is_guest' ? '1px solid #e67e22' : '',
                  borderRadius: '8px',
                  color: permissionStats.guest ? '#e67e22' : 'black',
                  backgroundColor: filter === 'is_guest' ? '#e67e2233' : '',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#e67e221A',
                  },
                }}
                onClick={() => toggleFilter('is_guest')}
              >
                <Stack minWidth="48px" alignItems="center">
                  <Typography fontSize={14} fontWeight={800}>
                    {permissionStats.guest}
                  </Typography>
                  <Typography fontSize={11} style={{ width: 'max-content' }}>
                    {i18n.t('app.user_countryside_modal.invited')}
                    {'s'}
                  </Typography>
                </Stack>
              </Button>
              {permissionStats.empty ? (
                <Stack alignItems="center">
                  <Typography
                    fontSize={14}
                    fontWeight={800}
                    style={{ color: permissionStats.empty ? 'red' : '' }}
                  >
                    {permissionStats.empty}
                  </Typography>
                  <Typography
                    fontSize={11}
                    color="textSecondary"
                    style={{ color: permissionStats.empty ? 'red' : '', width: 'max-content' }}
                  >
                    {i18n.t('app.user_countryside_modal.undefined_permissions')}
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
          {isListUsersLoading ? (
            <Stack flex={1} justifyContent="center" alignItems="center">
              <CircularProgress size={48} thickness={3} />
            </Stack>
          ) : (
            <TableContainer style={{ overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t('app.user')}</TableCell>
                    <TableCell>
                      <Stack width="100%" alignItems="start">
                        {i18n.t('app.user_modal.permission')}
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers?.map((user) => {
                    const permission = user.v_countrysides_permissions.at(0);
                    // if (!permission) return <></>; // TODO: si se descomenta esto se esconden los usuarios con permisos no detectados

                    const hasWritePermission = permission?.write_permission ?? false;
                    const hasFullPermission = permission?.full_permission ?? false;

                    const isInherit = permission?.inherited ?? false;
                    const isGuest = permission?.is_guest ?? false;

                    const disabledBtnStyles = {
                      PointerEvent: 'auto',
                      backgroundColor: '#e0e0e0',
                      '&:hover': {
                        backgroundColor: '#d0d0d0',
                      },
                      '&.Mui-disabled': {
                        color: '#aaa',
                      },
                    };
                    return (
                      <TableRow
                        key={user.id}
                        style={{
                          backgroundColor: user.id === userID && lockOwnPermission ? '#f4f4f4' : '',
                        }}
                      >
                        <TableCell>
                          <Stack gap={0.5}>
                            <Stack direction="row" gap={1} alignItems="center">
                              {user.id === userID ? (
                                <Tooltip
                                  title="Permisos correspondientes a su cuenta."
                                  placement="top"
                                  arrow
                                  disableInteractive
                                >
                                  <Typography fontSize={14} fontWeight={600} style={{ userSelect: 'none' }}>
                                    ⚜️
                                  </Typography>
                                </Tooltip>
                              ) : null}
                              <Typography fontSize={14} fontWeight={600}>
                                {user.fullname}
                              </Typography>
                              {isInherit ? (
                                <Tooltip
                                  title={i18n.t(
                                    'app.user_countryside_modal.tooltips.organization_inheritance'
                                  )}
                                  placement="top"
                                  arrow
                                  disableInteractive
                                >
                                  <Chip
                                    size="small"
                                    label={
                                      <Typography fontSize={11} color="textSecondary">
                                        {i18n.t('app.user_countryside_modal.inherited')}
                                      </Typography>
                                    }
                                    sx={{
                                      borderRadius: 1,
                                      backgroundColor: '#22a7f055',
                                      height: 20,
                                      userSelect: 'none',
                                    }}
                                  />
                                </Tooltip>
                              ) : null}
                              {isGuest ? (
                                <Tooltip
                                  title={i18n.t('app.user_countryside_modal.tooltips.invited')}
                                  placement="top"
                                  arrow
                                  disableInteractive
                                >
                                  <Chip
                                    size="small"
                                    label={
                                      <Typography fontSize={11} color="textSecondary">
                                        {i18n.t('app.user_countryside_modal.invited')}
                                      </Typography>
                                    }
                                    sx={{
                                      borderRadius: 1,
                                      backgroundColor: '#e67e2255',
                                      height: 20,
                                      userSelect: 'none',
                                    }}
                                  />
                                </Tooltip>
                              ) : null}
                            </Stack>
                            <Typography fontSize={12} color="textSecondary">
                              {user.email}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {permission ? (
                            <Stack direction="row" alignItems="center" style={{ position: 'relative' }}>
                              {loadingUsersID.has(user.id) ? (
                                <CircularProgress
                                  size={16}
                                  thickness={6}
                                  style={{ position: 'absolute', top: 8, left: -24 }}
                                />
                              ) : null}

                              <ButtonGroup
                                variant="contained"
                                aria-label="Basic button group"
                                style={{ position: 'relative' }}
                              >
                                {(user.id === userID && lockOwnPermission) ||
                                loadingUsersID.has(user.id) ||
                                read ? (
                                  <Box
                                    width="100%"
                                    height="100%"
                                    top={0}
                                    style={{
                                      backgroundColor: 'white',
                                      position: 'absolute',
                                      zIndex: 10,
                                      opacity: 0.5,
                                    }}
                                  />
                                ) : null}
                                <Box style={{ position: 'relative' }}>
                                  {(user.id !== userID || !lockOwnPermission) && !read ? (
                                    <Box
                                      width="100%"
                                      height="100%"
                                      top={0}
                                      left={-2}
                                      style={{
                                        backgroundColor: 'white',
                                        position: 'absolute',
                                        zIndex: 10,
                                        opacity: 0.5,
                                      }}
                                    />
                                  ) : null}

                                  <Button>
                                    <Typography fontSize={11} fontWeight={600} sx={{ color: 'white' }}>
                                      {i18n.t('app.silobag_share.view')}
                                    </Typography>
                                  </Button>
                                </Box>
                                <Button
                                  sx={hasWritePermission ? {} : disabledBtnStyles}
                                  onClick={() => {
                                    const updateData = {
                                      userID: user.id,
                                      permission: {
                                        read_permission: true,
                                        write_permission: !hasWritePermission,
                                        full_permission: false,
                                      },
                                    };
                                    if (user.id === userID) {
                                      setOwnUserChanges(updateData);
                                      return;
                                    }
                                    updateUserPermissions(updateData);
                                    activeUserIDLoader(user.id);
                                  }}
                                >
                                  <Typography
                                    fontSize={11}
                                    fontWeight={600}
                                    sx={{ color: hasWritePermission ? 'white' : '#aaa' }}
                                  >
                                    {i18n.t('app.silobag_share.edit')}
                                  </Typography>
                                </Button>
                                <Button
                                  sx={hasFullPermission ? {} : disabledBtnStyles}
                                  onClick={() => {
                                    const updateData = {
                                      userID: user.id,
                                      permission: {
                                        read_permission: true,
                                        write_permission: hasWritePermission,
                                        full_permission: !hasFullPermission,
                                      },
                                    };
                                    if (user.id === userID) {
                                      setOwnUserChanges(updateData);
                                      return;
                                    }
                                    updateUserPermissions(updateData);
                                    activeUserIDLoader(user.id);
                                  }}
                                >
                                  <Typography
                                    fontSize={11}
                                    fontWeight={600}
                                    sx={{ color: hasFullPermission ? 'white' : '#aaa' }}
                                  >
                                    {i18n.t('app.silobag_share.access_complete')}
                                  </Typography>
                                </Button>
                                {user.id === userID && !read ? (
                                  <Box
                                    style={{
                                      position: 'absolute',
                                      top: 2,
                                      right: -48,
                                      width: 32,
                                      height: 32,
                                    }}
                                  >
                                    {lockOwnPermission ? (
                                      <IconButton
                                        onClick={() => setLockOwnPermission(false)}
                                        sx={{ color: 'black', width: '16px', height: '16px', fontSize: 16 }}
                                      >
                                        🔐
                                      </IconButton>
                                    ) : (
                                      <IconButton
                                        onClick={() => setLockOwnPermission(true)}
                                        sx={{ color: 'black', width: '16px', height: '16px', fontSize: 16 }}
                                      >
                                        🔓
                                      </IconButton>
                                    )}
                                  </Box>
                                ) : null}
                              </ButtonGroup>
                            </Stack>
                          ) : (
                            <Box>
                              <Typography fontSize={12} color="textSecondary">
                                <i>{i18n.t('app.user_countryside_modal.undefined_permissions')}</i>
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
        <Stack width="100%" alignItems="end">
          <Button
            onClick={() => {
              onClose(needRefetch.current);
              if (hasChangeOwnPermissions.current) reFetchCountrysideListData();
            }}
          >
            {i18n.t('app.close')}
          </Button>
        </Stack>
      </Stack>
      {ownUserChanges ? (
        <MyModal2 open={Boolean(ownUserChanges)} onClose={() => setOwnUserChanges(null)}>
          <Stack gap={4}>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography fontSize={16} fontWeight={800}>
                {i18n.t('app.atention')}
              </Typography>
            </Stack>
            <Stack gap={1} justifyContent="center" alignItems="center">
              <Typography textAlign="center" fontSize={12} color="textSecondary">
                {i18n.t('app.user_countryside_modal.going_to_modificate')}
              </Typography>
              <Typography textAlign="center" fontSize={12} color="textSecondary">
                {i18n.t('app.user_countryside_modal.going_to_modificate_detail')}
              </Typography>

              <Typography textAlign="center" fontSize={12} color="textSecondary">
                {i18n.t('app.user_countryside_modal.revert_permission_detail')}
              </Typography>
            </Stack>
            <Typography fontSize={16} fontWeight={800} textAlign="center">
              {i18n.t('app.lab.warning_modal.continue')}
            </Typography>
          </Stack>
          <Stack direction="row" gap={2} justifyContent="end" alignItems="center">
            <Button
              variant="outlined"
              style={{
                width: 115,
                borderRadius: 100,
                textTransform: 'none',
                border: 'none',
                color: 'black',
              }}
              onClick={() => setOwnUserChanges(null)}
            >
              {i18n.t('app.close')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              color="error"
              style={{
                color: '#FFF',
                width: 115,
                borderRadius: 100,
                fontWeight: 600,
                textTransform: 'none',
              }}
              onClick={() => {
                updateUserPermissions(ownUserChanges);
                setOwnUserChanges(null);
              }}
            >
              {i18n.t('app.confirm')}
            </Button>
          </Stack>
        </MyModal2>
      ) : null}
    </MyModal>
  );
}

type ModalProps = {
  open: boolean;
  onClose: () => void;
  keepMounted?: boolean;
};

function MyModal({ open, onClose, keepMounted = false, children }: PropsWithChildren<ModalProps>) {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: '33%',
    // width: 'max-content',
    maxWidth: '96%',
    minHeight: '95vh',
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflowY: 'auto',
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal open={open} keepMounted={keepMounted} onClose={handleClose}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style}>{children}</Box>
      </Fade>
    </Modal>
  );
}

type ModalProps2 = {
  open: boolean;
  onClose: () => void;
};

function MyModal2({ open, onClose, children }: PropsWithChildren<ModalProps2>) {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 800,
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflowY: 'auto',
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal open={open} keepMounted onClose={handleClose}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style}>{children}</Box>
      </Fade>
    </Modal>
  );
}
